import type {
  AlteracaoDeAluno,
  CancelamentoDeAluno,
  ConsultaDeAlunos,
  DuplicadosDeTelefone,
  ExportacaoDoTitular,
  FichaDoAluno,
  IndicadoresDeAlunos,
  ListaDeAlunos,
  MudancaDeStatus,
  NovoAluno,
  TipoDeConsentimento,
} from '@arena/shared/schemas';
import { api } from './cliente.js';

/**
 * As chamadas de alunos — `#v-alunos`, `#m-aluno`, `#m-novo-aluno`.
 *
 * Os tipos são os de `@arena/shared/schemas`, os mesmos que o backend usa
 * para validar e responder. `listar` recebe a query já validada por
 * `zConsultaDeAlunos` — o formulário e a URL passam por ali antes de
 * chegar aqui.
 *
 * Ver: Arena_backend/Docs/plano/08-alunos.md
 */
export const alunosApi = {
  listar(consulta: Partial<ConsultaDeAlunos> = {}): Promise<ListaDeAlunos> {
    const query = new URLSearchParams();
    if (consulta.busca) query.set('busca', consulta.busca);
    if (consulta.status) query.set('status', consulta.status);
    if (consulta.cursor) query.set('cursor', consulta.cursor);
    if (consulta.limite) query.set('limite', String(consulta.limite));
    const texto = query.toString();
    return api<ListaDeAlunos>(`/alunos${texto ? `?${texto}` : ''}`);
  },

  indicadores(): Promise<IndicadoresDeAlunos> {
    return api<IndicadoresDeAlunos>('/alunos/indicadores');
  },

  /** "Este telefone já tem cadastro?" — o aviso de `#m-novo-aluno`. */
  duplicados(telefone: string, ignorar?: string): Promise<DuplicadosDeTelefone> {
    const query = new URLSearchParams({ telefone });
    if (ignorar) query.set('ignorar', ignorar);
    return api<DuplicadosDeTelefone>(`/alunos/duplicados?${query.toString()}`);
  },

  criar(dados: NovoAluno): Promise<FichaDoAluno> {
    return api<FichaDoAluno>('/alunos', { metodo: 'POST', corpo: dados });
  },

  ficha(id: string): Promise<FichaDoAluno> {
    return api<FichaDoAluno>(`/alunos/${id}`);
  },

  alterar(id: string, dados: AlteracaoDeAluno): Promise<FichaDoAluno> {
    return api<FichaDoAluno>(`/alunos/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  ativar(id: string, dados: MudancaDeStatus): Promise<FichaDoAluno> {
    return api<FichaDoAluno>(`/alunos/${id}/ativar`, { metodo: 'POST', corpo: dados });
  },

  inativar(id: string, dados: MudancaDeStatus): Promise<FichaDoAluno> {
    return api<FichaDoAluno>(`/alunos/${id}/inativar`, { metodo: 'POST', corpo: dados });
  },

  /** Cancelar exige motivo — o servidor recusa sem ele. */
  cancelar(id: string, dados: CancelamentoDeAluno): Promise<FichaDoAluno> {
    return api<FichaDoAluno>(`/alunos/${id}/cancelar`, { metodo: 'POST', corpo: dados });
  },

  registrarConsentimento(id: string, tipo: TipoDeConsentimento): Promise<FichaDoAluno> {
    return api<FichaDoAluno>(`/alunos/${id}/consentimentos`, { metodo: 'POST', corpo: { tipo } });
  },

  /** Só o de imagem é revogável — o servidor recusa o do termo. */
  revogarConsentimento(id: string, tipo: TipoDeConsentimento): Promise<FichaDoAluno> {
    return api<FichaDoAluno>(`/alunos/${id}/consentimentos/revogar`, {
      metodo: 'POST',
      corpo: { tipo },
    });
  },

  /** Irreversível — a confirmação é o nome completo do aluno, digitado. */
  anonimizar(id: string, confirmacao: string): Promise<FichaDoAluno> {
    return api<FichaDoAluno>(`/alunos/${id}/anonimizar`, {
      metodo: 'POST',
      corpo: { confirmacao },
    });
  },

  /** "Exportar dados desta pessoa" — aba LGPD. */
  exportar(id: string): Promise<ExportacaoDoTitular> {
    return api<ExportacaoDoTitular>(`/alunos/${id}/exportacao`);
  },
};
